import { useMemo, useRef, useEffect } from 'react';
import { useFrame, useLoader } from '@react-three/fiber';
import * as THREE from 'three';

// Card dimensions (credit card ratio)
const CARD_WIDTH = 2.125;
const CARD_HEIGHT = 3.37;
const CARD_THICKNESS = 0.03;
const CORNER_RADIUS = 0.1;
const FACE_OFFSET = 0.001; // Small offset to prevent z-fighting with edge caps

// Sleeve dimensions
const SLEEVE_PADDING = 0.03; // Extra space around card
const SLEEVE_LIP = 0.12; // How much shorter the front is (open pocket)

export type CardColor = 'white' | 'black';
export type WhiteTreatment = 'glossy' | 'matte';

// Custom shader material for UV print simulation
class UVPrintMaterial extends THREE.MeshStandardMaterial {
  private _whiteTreatment: WhiteTreatment = 'glossy';
  private _cardColor: CardColor = 'white';

  set whiteTreatment(value: WhiteTreatment) {
    this._whiteTreatment = value;
    this.needsUpdate = true;
  }

  get whiteTreatment() {
    return this._whiteTreatment;
  }

  set cardColor(value: CardColor) {
    this._cardColor = value;
    this.needsUpdate = true;
  }

  get cardColor() {
    return this._cardColor;
  }

  onBeforeCompile(shader: { uniforms: Record<string, { value: unknown }>; fragmentShader: string; vertexShader: string }) {
    // Add uniforms for treatment and card color
    // treatment: 0.0 = glossy, 1.0 = matte
    // isBlackCard: 0.0 = white card, 1.0 = black card
    shader.uniforms.treatment = { value: this._whiteTreatment === 'glossy' ? 0.0 : 1.0 };
    shader.uniforms.isBlackCard = { value: this._cardColor === 'black' ? 1.0 : 0.0 };

    // Modify fragment shader to add uniforms
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <common>',
      `#include <common>
      uniform float treatment;
      uniform float isBlackCard;`
    );

    // Perturb normals for UV print effects (before lighting calculations)
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <normal_fragment_maps>',
      `#include <normal_fragment_maps>

      #ifdef USE_MAP
        vec4 bandTexel = texture2D(map, vMapUv);

        // Calculate luminance
        float luminance = dot(bandTexel.rgb, vec3(0.299, 0.587, 0.114));

        // Check if pure white or pure black (unprinted areas depend on card color)
        float whiteThreshold = 0.95;
        float blackThreshold = 0.05;
        bool isPureWhite = bandTexel.r > whiteThreshold &&
                          bandTexel.g > whiteThreshold &&
                          bandTexel.b > whiteThreshold;
        bool isPureBlack = luminance < blackThreshold;

        // Determine if this pixel is the "base color" of the card
        // White card: pure white is base color (unprinted or white ink)
        // Black card: pure black is base color (unprinted or black ink)
        bool isBaseColor = isBlackCard > 0.5 ? isPureBlack : isPureWhite;

        // Ink density calculation depends on card color
        // White card: darker = more ink
        // Black card: lighter = more ink (white/colored inks on black plastic)
        float inkDensity = isBlackCard > 0.5 ? luminance : (1.0 - luminance);

        // === 1. Print pass banding (horizontal ridges) ===
        // Base color areas: banding only if matte (treatment=1), none if glossy (treatment=0)
        // Printed ink areas: always have subtle banding (ink is always printed)
        float baseBandingStrength = treatment;  // 0 for glossy (unprinted), 1 for matte (printed base ink)
        float printedBandingStrength = 0.25;    // Printed ink always has subtle banding
        float bandingStrength = isBaseColor ? baseBandingStrength : printedBandingStrength;

        float bandFrequency = 300.0;
        float bandPhase = vMapUv.y * bandFrequency * 6.28318;
        float bandSlope = cos(bandPhase) * bandingStrength * 0.12;

        // === 2. Ink height texture (more ink = more raised = sparkly micro-facets) ===
        // Sample neighboring pixels to compute gradient for raised ink effect
        float texelSize = 1.0 / 512.0;
        float leftLum = dot(texture2D(map, vMapUv - vec2(texelSize, 0.0)).rgb, vec3(0.299, 0.587, 0.114));
        float rightLum = dot(texture2D(map, vMapUv + vec2(texelSize, 0.0)).rgb, vec3(0.299, 0.587, 0.114));
        float topLum = dot(texture2D(map, vMapUv + vec2(0.0, texelSize)).rgb, vec3(0.299, 0.587, 0.114));
        float bottomLum = dot(texture2D(map, vMapUv - vec2(0.0, texelSize)).rgb, vec3(0.299, 0.587, 0.114));

        // Gradient direction depends on card color
        // White card: dark edges are raised (negative gradient = raised edge)
        // Black card: light edges are raised (positive gradient = raised edge)
        float dIdx = isBlackCard > 0.5 ? (rightLum - leftLum) : (leftLum - rightLum);
        float dIdy = isBlackCard > 0.5 ? (topLum - bottomLum) : (bottomLum - topLum);

        // Raised ink effect - stronger on denser ink areas
        // Base color areas: only raised if matte (printed ink), not if glossy (bare plastic)
        // Printed ink areas: always raised
        float raiseStrength = inkDensity * inkDensity * 8.0;
        raiseStrength *= isBaseColor ? treatment : 1.0;

        // Combine banding + raised ink into normal perturbation
        vec3 normalPerturbation = vec3(
          dIdx * raiseStrength,
          bandSlope + dIdy * raiseStrength,
          0.0
        );

        normal = normalize(normal + normalPerturbation);
      #endif`
    );

    // After the map is sampled, adjust roughness based on whether pixel is printed or not
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <roughnessmap_fragment>',
      `#include <roughnessmap_fragment>

      #ifdef USE_MAP
        vec4 texelColor = texture2D(map, vMapUv);

        // Calculate luminance
        float lum = dot(texelColor.rgb, vec3(0.299, 0.587, 0.114));

        // Check if pure white or pure black
        float rWhiteThreshold = 0.95;
        float rBlackThreshold = 0.05;
        bool rIsPureWhite = texelColor.r > rWhiteThreshold &&
                           texelColor.g > rWhiteThreshold &&
                           texelColor.b > rWhiteThreshold;
        bool rIsPureBlack = lum < rBlackThreshold;

        // Determine if this is the base color of the card
        bool rIsBaseColor = isBlackCard > 0.5 ? rIsPureBlack : rIsPureWhite;

        // Ink density depends on card color
        float ink = isBlackCard > 0.5 ? lum : (1.0 - lum);

        // Roughness values for printed ink areas (always the same regardless of treatment)
        // More ink = more raised = catches light = slightly shinier surface
        float baseInkRoughness = 0.65;
        float denseInkRoughness = 0.45;  // Dense ink areas are shinier (raised glossy ink)
        float inkRoughness = mix(baseInkRoughness, denseInkRoughness, ink * ink);

        // Base color area roughness depends on treatment:
        // Glossy = bare plastic surface
        // Matte = printed base color ink
        float glossyWhitePlastic = 0.12;  // White plastic is very shiny
        float glossyBlackPlastic = 0.5;   // Black plastic is less reflective
        float glossyPlastic = isBlackCard > 0.5 ? glossyBlackPlastic : glossyWhitePlastic;
        float matteBaseInk = 0.72;        // Matte printed ink

        // Base color: interpolate between glossy plastic and matte ink based on treatment
        // Printed ink: always use ink roughness (unaffected by treatment)
        float baseColorRoughness = mix(glossyPlastic, matteBaseInk, treatment);
        roughnessFactor = rIsBaseColor ? baseColorRoughness : inkRoughness;
      #endif`
    );
  }
}

interface CardModelProps {
  frontImageUrl?: string;
  backImageUrl?: string;
  cardColor?: CardColor;
  whiteTreatment?: WhiteTreatment;
  showCover?: boolean;
  rotationY: React.MutableRefObject<number>;
  rotationX: React.MutableRefObject<number>;
  positionY?: React.MutableRefObject<number>;
  scale?: React.MutableRefObject<number>;
}

function CardFace({
  geometry,
  imageUrl,
  isBack = false,
  cardColor = 'white',
  treatment = 'glossy'
}: {
  geometry: THREE.BufferGeometry;
  imageUrl: string;
  isBack?: boolean;
  cardColor?: CardColor;
  treatment?: WhiteTreatment;
}) {
  const texture = useLoader(THREE.TextureLoader, imageUrl);

  useMemo(() => {
    if (texture) {
      texture.flipY = true;
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.needsUpdate = true;
    }
  }, [texture]);

  // Create material instance
  const material = useMemo(() => {
    const mat = new UVPrintMaterial({
      color: '#ffffff',
      metalness: 0.05,
      roughness: 0.5,
      side: THREE.FrontSide,
    });
    mat.whiteTreatment = treatment;
    mat.cardColor = cardColor;
    return mat;
  }, [treatment, cardColor]);

  // Update texture when it changes
  useEffect(() => {
    material.map = texture;
    material.needsUpdate = true;
  }, [material, texture]);

  // Clean up material on unmount
  useEffect(() => {
    return () => {
      material.dispose();
    };
  }, [material]);

  return (
    <mesh
      geometry={geometry}
      position={[0, 0, isBack ? -FACE_OFFSET : CARD_THICKNESS + FACE_OFFSET]}
      rotation={isBack ? [0, Math.PI, 0] : [0, 0, 0]}
      castShadow
      receiveShadow
      material={material}
    />
  );
}

function CardFacePlain({
  geometry,
  isBack = false,
  cardColor = 'white'
}: {
  geometry: THREE.BufferGeometry;
  isBack?: boolean;
  cardColor?: CardColor;
}) {
  const color = cardColor === 'black' ? '#1a1a1a' : '#e8e8e8';
  return (
    <mesh
      geometry={geometry}
      position={[0, 0, isBack ? -FACE_OFFSET : CARD_THICKNESS + FACE_OFFSET]}
      rotation={isBack ? [0, Math.PI, 0] : [0, 0, 0]}
      castShadow
      receiveShadow
    >
      <meshStandardMaterial color={color} metalness={0} roughness={0.6} side={THREE.FrontSide} />
    </mesh>
  );
}

function CardEdge({ geometry, cardColor = 'white' }: { geometry: THREE.BufferGeometry; cardColor?: CardColor }) {
  const color = cardColor === 'black' ? '#1a1a1a' : '#ffffff';
  return (
    <mesh geometry={geometry} castShadow receiveShadow>
      <meshStandardMaterial color={color} metalness={0} roughness={0.6} side={THREE.DoubleSide} />
    </mesh>
  );
}

// Create a procedural noise texture for sleeve imperfections
function createNoiseTexture(width: number, height: number): THREE.DataTexture {
  const size = width * height;
  const data = new Uint8Array(size);

  for (let i = 0; i < size; i++) {
    // Base noise
    let noise = Math.random();

    // Add some larger-scale variation (smudges/fingerprints)
    const x = i % width;
    const y = Math.floor(i / width);
    const smudgeNoise = Math.sin(x * 0.05) * Math.cos(y * 0.03) * 0.15;

    // Occasional scratches (horizontal streaks)
    const scratchChance = Math.random();
    const scratch = scratchChance > 0.97 ? 0.3 : 0;

    // Combine: mostly smooth with subtle variation
    noise = 0.3 + noise * 0.25 + smudgeNoise + scratch;
    data[i] = Math.min(255, Math.max(0, noise * 255));
  }

  const texture = new THREE.DataTexture(data, width, height, THREE.RedFormat);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.needsUpdate = true;
  return texture;
}

// Plastic sleeve with open top pocket - tapered edges where front/back meet
function CardSleeve() {
  const cardScale = 0.9;
  const sleeveW = CARD_WIDTH * cardScale + SLEEVE_PADDING * 2;
  const sleeveH = CARD_HEIGHT * cardScale + SLEEVE_PADDING * 2;
  const frontH = sleeveH - SLEEVE_LIP;

  // Create noise texture for imperfections
  const noiseTexture = useMemo(() => createNoiseTexture(128, 128), []);

  // Clear plastic material with refraction and imperfections
  const sleeveMaterial = useMemo(() => {
    return new THREE.MeshPhysicalMaterial({
      color: '#ffffff',
      transparent: true,
      opacity: 0.15,
      roughness: 0.08, // Slightly higher base roughness
      roughnessMap: noiseTexture, // Adds variation
      metalness: 0,
      transmission: 0.8,
      ior: 1.45,
      thickness: 0.15,
      clearcoat: 0.3,
      clearcoatRoughness: 0.15, // Slightly rougher clearcoat
      clearcoatRoughnessMap: noiseTexture, // Scratches on surface
      side: THREE.DoubleSide,
      depthWrite: false,
    });
  }, [noiseTexture]);

  const gap = 0.025;
  const backZ = -gap;
  const frontZ = CARD_THICKNESS + gap;

  // Create tapered edge geometries that curve from back to front
  const { leftEdgeGeo, rightEdgeGeo, bottomEdgeGeo } = useMemo(() => {
    const segments = 6;

    // Helper to create a curved strip from back to front
    // pinchDirection: 1 for left edge (pinch in +X), -1 for right edge (pinch in -X), 0 for bottom
    const createEdgeStrip = (length: number, isVertical: boolean, pinchDirection: number = 1) => {
      const positions: number[] = [];
      const normals: number[] = [];
      const indices: number[] = [];

      const taperAmount = 0.015; // How much to pinch inward

      for (let i = 0; i <= segments; i++) {
        const t = i / segments;
        const z = backZ + (frontZ - backZ) * t;

        // Pinch inward in the middle - forms a V-shape cross-section
        const pinch = -Math.sin(t * Math.PI) * taperAmount * pinchDirection;

        if (isVertical) {
          // Vertical edge (left/right) - runs along Y axis
          positions.push(pinch, -length / 2, z);
          positions.push(pinch, length / 2, z);
          // Normal: blend between facing back (-Z) and facing front (+Z)
          const nz = t < 0.5 ? -1 : 1;
          const nx = -Math.cos(t * Math.PI) * 0.5 * pinchDirection;
          normals.push(nx, 0, nz);
          normals.push(nx, 0, nz);
        } else {
          // Horizontal edge (bottom) - runs along X axis, pinches in Y
          positions.push(-length / 2, pinch, z);
          positions.push(length / 2, pinch, z);
          // Normal: blend between facing back (-Z) and facing front (+Z)
          const nz = t < 0.5 ? -1 : 1;
          const ny = -Math.cos(t * Math.PI) * 0.5 * pinchDirection;
          normals.push(0, ny, nz);
          normals.push(0, ny, nz);
        }
      }

      // Create triangles connecting the strips
      for (let i = 0; i < segments; i++) {
        const a = i * 2;
        const b = i * 2 + 1;
        const c = (i + 1) * 2;
        const d = (i + 1) * 2 + 1;

        indices.push(a, c, b);
        indices.push(b, c, d);
      }

      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      geo.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
      geo.setIndex(indices);
      return geo;
    };

    return {
      leftEdgeGeo: createEdgeStrip(sleeveH, true, 1),    // Pinch toward +X (inward)
      rightEdgeGeo: createEdgeStrip(sleeveH, true, -1),  // Pinch toward -X (inward)
      bottomEdgeGeo: createEdgeStrip(sleeveW, false, 1), // Pinch toward +Y (inward/up)
    };
  }, [sleeveH, sleeveW, backZ, frontZ]);

  return (
    <group>
      {/* Back panel - full height */}
      <mesh position={[0, 0, backZ]} material={sleeveMaterial}>
        <planeGeometry args={[sleeveW, sleeveH]} />
      </mesh>

      {/* Front panel - same height as back */}
      <mesh position={[0, 0, frontZ]} material={sleeveMaterial}>
        <planeGeometry args={[sleeveW, sleeveH]} />
      </mesh>

      {/* Left tapered edge */}
      <mesh
        geometry={leftEdgeGeo}
        position={[-sleeveW / 2, 0, 0]}
        material={sleeveMaterial}
      />

      {/* Right tapered edge */}
      <mesh
        geometry={rightEdgeGeo}
        position={[sleeveW / 2, 0, 0]}
        material={sleeveMaterial}
      />

      {/* Bottom tapered edge */}
      <mesh
        geometry={bottomEdgeGeo}
        position={[0, -sleeveH / 2, 0]}
        material={sleeveMaterial}
      />
    </group>
  );
}

function useCardGeometries() {
  return useMemo(() => {
    const cardScale = 0.9;
    const cardW = CARD_WIDTH * cardScale;
    const cardH = CARD_HEIGHT * cardScale;

    // Create rounded rectangle shape
    const cardShape = new THREE.Shape();
    cardShape.moveTo(-cardW / 2 + CORNER_RADIUS, -cardH / 2);
    cardShape.lineTo(cardW / 2 - CORNER_RADIUS, -cardH / 2);
    cardShape.quadraticCurveTo(cardW / 2, -cardH / 2, cardW / 2, -cardH / 2 + CORNER_RADIUS);
    cardShape.lineTo(cardW / 2, cardH / 2 - CORNER_RADIUS);
    cardShape.quadraticCurveTo(cardW / 2, cardH / 2, cardW / 2 - CORNER_RADIUS, cardH / 2);
    cardShape.lineTo(-cardW / 2 + CORNER_RADIUS, cardH / 2);
    cardShape.quadraticCurveTo(-cardW / 2, cardH / 2, -cardW / 2, cardH / 2 - CORNER_RADIUS);
    cardShape.lineTo(-cardW / 2, -cardH / 2 + CORNER_RADIUS);
    cardShape.quadraticCurveTo(-cardW / 2, -cardH / 2, -cardW / 2 + CORNER_RADIUS, -cardH / 2);

    // Face geometry (flat plane for textures)
    const faceGeo = new THREE.ShapeGeometry(cardShape);

    // Fix UVs for proper texture mapping
    const positions = faceGeo.attributes.position;
    const uvs = faceGeo.attributes.uv;

    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;

    for (let i = 0; i < positions.count; i++) {
      const x = positions.getX(i);
      const y = positions.getY(i);
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }

    const rangeX = maxX - minX;
    const rangeY = maxY - minY;

    for (let i = 0; i < positions.count; i++) {
      const x = positions.getX(i);
      const y = positions.getY(i);
      const u = (x - minX) / rangeX;
      const v = (y - minY) / rangeY;
      uvs.setXY(i, u, v);
    }
    uvs.needsUpdate = true;

    // Edge geometry (extruded for thickness)
    const edgeGeo = new THREE.ExtrudeGeometry(cardShape, {
      depth: CARD_THICKNESS,
      bevelEnabled: false,
    });

    return { faceGeo, edgeGeo };
  }, []);
}

export default function CardModel({
  frontImageUrl,
  backImageUrl,
  cardColor = 'white',
  whiteTreatment = 'glossy',
  showCover = false,
  rotationY,
  rotationX,
  positionY,
  scale,
}: CardModelProps) {
  const groupRef = useRef<THREE.Group>(null);
  const { faceGeo, edgeGeo } = useCardGeometries();

  useFrame(() => {
    if (groupRef.current) {
      groupRef.current.rotation.y = rotationY.current;
      groupRef.current.rotation.x = rotationX.current;
      if (positionY) {
        groupRef.current.position.y = positionY.current;
      }
      if (scale) {
        const s = scale.current;
        groupRef.current.scale.set(s, s, s);
      }
    }
  });

  return (
    <group ref={groupRef}>
      {/* Card edge/body */}
      <CardEdge geometry={edgeGeo} cardColor={cardColor} />

      {/* Front face */}
      {frontImageUrl ? (
        <CardFace geometry={faceGeo} imageUrl={frontImageUrl} cardColor={cardColor} treatment={whiteTreatment} />
      ) : (
        <CardFacePlain geometry={faceGeo} cardColor={cardColor} />
      )}

      {/* Back face */}
      {backImageUrl ? (
        <CardFace geometry={faceGeo} imageUrl={backImageUrl} isBack cardColor={cardColor} treatment={whiteTreatment} />
      ) : (
        <CardFacePlain geometry={faceGeo} isBack cardColor={cardColor} />
      )}

      {/* Plastic sleeve/cover */}
      {showCover && <CardSleeve />}
    </group>
  );
}
